import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../CompanyContext';
import { customersAPI } from '../../services/api';
import { Customer, CustomerStatus, CustomerType } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  UserGroupIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const CustomersList: React.FC = () => {
  const { selectedCompany } = useCompany();
  const { t, i18n } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    if (selectedCompany) {
      fetchCustomers();
    }
  }, [selectedCompany, statusFilter, typeFilter]);

  const fetchCustomers = async () => {
    if (!selectedCompany) return;
    
    try {
      setLoading(true);
      setError(null);
      let data: Customer[];
      
      if (searchTerm.trim()) {
        data = await customersAPI.searchCustomers(selectedCompany.id, searchTerm);
      } else if (statusFilter === 'active') {
        data = await customersAPI.getActiveByCompany(selectedCompany.id);
      } else {
        data = await customersAPI.getByCompany(selectedCompany.id);
      }
      
      // Apply type filter
      if (typeFilter !== 'all') {
        data = data.filter(customer => customer.type === typeFilter);
      }
      
      setCustomers(data);
    } catch (err) {
      setError(t('customers.errorTitle'));
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCustomers();
  };

  const getStatusIcon = (status: CustomerStatus) => {
    switch (status) {
      case CustomerStatus.ACTIVE:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case CustomerStatus.INACTIVE:
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
      case CustomerStatus.BLOCKED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: CustomerStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case CustomerStatus.ACTIVE:
        return `${baseClasses} bg-green-100 text-green-800`;
      case CustomerStatus.INACTIVE:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case CustomerStatus.BLOCKED:
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getTypeIcon = (type: CustomerType) => {
    switch (type) {
      case CustomerType.BUSINESS:
        return <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />;
      case CustomerType.INDIVIDUAL:
        return <UserIcon className="h-4 w-4 text-green-600" />;
      default:
        return <UserIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatCurrency = (amount: number) => {
    const currency = selectedCompany?.currency?.code || 'USD';
    console.log('Currency:', currency);
    console.log('Amount:', selectedCompany);
    return new Intl.NumberFormat(i18n.language || 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString(i18n.language || 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDelete = async (customerId: string) => {
    if (!window.confirm(t('customers.actions.confirmDelete'))) {
      return;
    }

    try {
      await customersAPI.delete(customerId);
      fetchCustomers();
    } catch (err) {
      setError(t('customers.actions.delete'));
      console.error('Error deleting customer:', err);
    }
  };

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('customers.noCompanyTitle')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('customers.noCompanyText')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
          <p className="text-gray-600">{t('customers.manageFor', { company: selectedCompany.name })}</p>
        </div>
        <Link
          to="/customers/new"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t('customers.add')}
        </Link>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSearch} className="flex">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('customers.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button
                type="submit"
                className="ml-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                {t('customers.search')}
              </button>
            </form>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{t('customers.statusFilter')}</option>
              <option value="active">{t('customers.statuses.ACTIVE')}</option>
              <option value="inactive">{t('customers.statuses.INACTIVE')}</option>
              <option value="blocked">{t('customers.statuses.BLOCKED')}</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{t('customers.typeFilter')}</option>
              <option value="individual">{t('customers.types.INDIVIDUAL')}</option>
              <option value="business">{t('customers.types.BUSINESS')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Customers List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('customers.emptyTitle')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? t('customers.emptySearchAdjust') : t('customers.emptyAll')}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <Link
                  to="/customers/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('customers.emptyCta')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customers.table.customer')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customers.table.type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customers.table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customers.table.balance')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customers.table.location')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customers.table.created')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customers.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(customer.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {customer.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {customer.email}
                          </div>
                          {customer.phone && (
                            <div className="text-sm text-gray-500">
                              {customer.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getTypeIcon(customer.type)}
                        <span className="ml-2 text-sm text-gray-900">
                          {t(`customers.types.${String(customer.type).toUpperCase()}`)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(customer.status)}>
                        {t(`customers.statuses.${String(customer.status).toUpperCase()}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div>{t('customers.table.balance')}: {formatCurrency(customer.currentBalance)}</div>
                        <div className="text-gray-500">
                          {t('customers.table.limit')}: {formatCurrency(customer.creditLimit)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {customer.city && customer.state ? (
                        <div>
                          <div>{customer.city}, {customer.state}</div>
                          <div className="text-gray-500">{customer.country}</div>
                        </div>
                      ) : (
                        <span className="text-gray-500">{t('products.na')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(customer.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/customers/${customer.id}`}
                          className="text-primary-600 hover:text-primary-900"
                          title={t('customers.actions.view')}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/customers/${customer.id}/edit`}
                          className="text-gray-600 hover:text-gray-900"
                          title={t('customers.actions.edit')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(customer.id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('customers.actions.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersList;
